import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';

import { URLS } from '../../resources/constants';
import { useQbSyncApi } from '../../resources/hooks/api/useQbSyncApiHook';
import SAlert from '../../components/SAlert';
import {
    APPLY_STATUS_TEXT, CHANGE_TEXT, dateTimeLabel, DECISION_TEXT, entityLabel, isSelectable, monthLabel,
    MONTH_STATUS_TEXT, REASON_TEXT, selectedMonths, selectionGap, sourceName, STATUS_PILL, SyncMonth,
} from './qbSyncUtils';
import { needsQboReconnect, startQboReconnect } from './qboReconnect';

const POLL_MS = 4000;

/**
 * Review QuickBooks changes (QuickBooks Online and Desktop).
 * Shows the months that changed since the last sync -- the user never picks a
 * "from" date -- lets the user tick / untick months and view each month's
 * transactions, then applies in the background. Nothing is posted before Apply.
 */
const QbSyncReview: React.FC = () => {
    const { t } = useTranslation('scenes.QBSync');
    // Dynamic keys (status / reason codes): always render as plain text.
    const tx = (key: string, fallback: string, opts?: any): string => String(t(key, fallback, opts));
    const navigate = useNavigate();
    const { sessionId: sessionParam } = useParams();
    const sessionId = Number(sessionParam);
    const { getSession, getChanges, setMonths, applySession, cancelSession } = useQbSyncApi();

    const [session, setSession] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<Record<string, any[] | 'loading'>>({});
    const [confirmApply, setConfirmApply] = useState<boolean>(false);
    const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
    const timer = useRef<any>(null);

    const load = useCallback(() => {
        getSession(sessionId, (_m: string, resp: any) => {
            setSession(resp?.data?.data || null);
            setLoading(false);
        }, (msg: string) => {
            toast.error(msg);
            setLoading(false);
        });
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(load, [load]);

    // Keep refreshing while QuickBooks is being read or the changes are being applied.
    useEffect(() => {
        clearTimeout(timer.current);
        if (session && ['collecting', 'applying'].includes(session.status)) {
            timer.current = setTimeout(load, POLL_MS);
        }
        return () => clearTimeout(timer.current);
    }, [session, load]);

    const months: SyncMonth[] = session?.months || [];
    const ready = session?.status === 'ready';
    const name = sourceName(session?.source);
    const importedThroughMonth = session?.imported_through ? String(session.imported_through).slice(0, 7) : null;
    const gap = selectionGap(months, importedThroughMonth);
    const chosen = selectedMonths(months);

    const toggleMonth = (m: SyncMonth) => {
        setBusy(true);
        setMonths(sessionId, [{ month: m.month, selected: !m.selected }], (_m: string, resp: any) => {
            setSession(resp?.data?.data || session);
            setBusy(false);
        }, (msg: string) => {
            toast.error(msg);
            setBusy(false);
        });
    };

    const toggleView = (month: string) => {
        if (open[month]) {
            const next = { ...open };
            delete next[month];
            setOpen(next);
            return;
        }
        setOpen({ ...open, [month]: 'loading' });
        getChanges(sessionId, month, (_m: string, resp: any) => {
            setOpen((o) => ({ ...o, [month]: resp?.data?.data?.changes || [] }));
        }, (msg: string) => {
            toast.error(msg);
            setOpen((o) => { const n = { ...o }; delete n[month]; return n; });
        });
    };

    const apply = () => {
        setConfirmApply(false);
        setBusy(true);
        applySession(sessionId, () => {
            setBusy(false);
            load();
        }, (msg: string) => {
            toast.error(msg);
            setBusy(false);
        });
    };

    const cancel = () => {
        setConfirmCancel(false);
        cancelSession(sessionId, () => navigate(URLS.DASHBOARD), (msg: string) => toast.error(msg));
    };

    if (loading) return <div className="main-container flex-grow-1 p-4"><Spinner animation="border" role="status" /></div>;
    if (!session) return <div className="main-container flex-grow-1 p-4">{t('review.notFound', 'This re-sync was not found.')}</div>;

    const totals = session.totals || {};

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3 d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                        <h1 className="h5 font-weight-700 mb-1">{t('review.title', 'Review QuickBooks changes')}</h1>
                        <div className="text-muted small">
                            {name}
                            {' · '}{t('review.since', 'changes since {{when}}', { when: dateTimeLabel(session.changes_since) || t('review.firstImport', 'the first import') })}
                            {' · '}{t('review.syncThrough', 'Sync through {{month}}', { month: monthLabel(session.sync_through) })}
                        </div>
                    </div>
                    <span className="badge bg-light text-dark border">{t('review.nothingYet', 'Nothing is applied until you click Apply')}</span>
                </div>

                {session.status === 'collecting' && (
                    <div className="alert alert-info d-flex align-items-center gap-2" role="status">
                        <Spinner animation="border" size="sm" />
                        {session.source === 'qbd'
                            ? t('review.waitingConnector', 'Waiting for the desktop connector: on the computer with QuickBooks Desktop, open the RevRex QuickBooks Connector and click "Sync changes". This page updates by itself.')
                            : t('review.collecting', 'Reading changes from QuickBooks Online…')}
                    </div>
                )}
                {session.status === 'applying' && (
                    <div className="alert alert-info d-flex align-items-center gap-2" role="status">
                        <Spinner animation="border" size="sm" />{t('review.applying', 'Applying the selected changes… you can leave this page; it continues in the background.')}
                    </div>
                )}
                {session.status === 'applied' && (
                    <div className="alert alert-success" role="status">
                        {t('review.applied', 'Done. The selected changes were imported.')}{' '}
                        {session.reconciled_through && (
                            <span>{tx('review.reconciledThrough', 'Bank months are reconciled and closed through {{month}}.',
                                { month: monthLabel(session.reconciled_through) })}</span>
                        )}
                        <button type="button" className="btn btn-sm btn-success ms-2" onClick={() => navigate(URLS.DASHBOARD)}>
                            {t('review.backToDashboard', 'Back to dashboard')}
                        </button>
                    </div>
                )}
                {session.status === 'applied' && (session.months_to_review || []).length > 0 && (
                    <div className="alert alert-warning" role="status">
                        {session.source === 'qbo'
                            ? tx('review.toReviewQbo', '{{months}} imported but not closed yet: the import check found differences. Review and submit them on the QuickBooks import screen.',
                                { months: session.months_to_review.map((m: string) => monthLabel(m)).join(', ') })
                            : tx('review.toReviewQbd', '{{months}} imported but not closed yet: some transactions were not imported. Run a new re-sync to bring them in.',
                                { months: session.months_to_review.map((m: string) => monthLabel(m)).join(', ') })}
                        {session.source === 'qbo' && (
                            <button type="button" className="btn btn-sm btn-warning ms-2" onClick={() => navigate(URLS.QUICKBOOK_DASHBOARD)}>
                                {t('review.openImportScreen', 'Open QuickBooks import')}
                            </button>
                        )}
                    </div>
                )}
                {session.status === 'failed' && (
                    <div className="alert alert-danger" role="alert">
                        {t('review.failed', 'This re-sync did not complete. Nothing after the last successful month was changed; start a new re-sync to try again.')}
                        {needsQboReconnect(session.error_message) ? (
                            <div className="mt-2">
                                <div className="small mb-2">{t('statusReason.qbo_reconnect_required', 'Your QuickBooks Online connection has expired. Reconnect to re-sync.')}</div>
                                <button type="button" className="btn btn-sm btn-warning"
                                        onClick={() => startQboReconnect(session.branch_id, () =>
                                            toast.error(tx('panel.reconnectFailed', 'Could not open the QuickBooks Online sign-in. Please try again.')))}>
                                    {t('panel.reconnect', 'Reconnect QuickBooks Online')}
                                </button>
                            </div>
                        ) : session.error_message === 'qb_sync.collect_timed_out' ? (
                            <div className="small mt-1">
                                {tx('error.collect_timed_out', 'QuickBooks was not read within {{minutes}} minutes, so this re-sync was stopped. Nothing was imported. Start a new re-sync to try again.',
                                    { minutes: session.collect_timeout_minutes || 10 })}
                            </div>
                        ) : session.error_message && <div className="small mt-1">{session.error_message}</div>}
                    </div>
                )}
                {session.status === 'cancelled' && (
                    <div className="alert alert-secondary" role="status">{t('review.cancelled', 'This re-sync was cancelled. Nothing was imported.')}</div>
                )}

                {months.length > 0 && (
                    <div className="d-flex gap-3 flex-wrap mb-3" aria-label={t('review.totals', 'Totals')}>
                        {[['new', t('review.new', 'New')], ['edited', t('review.edited', 'Edited')], ['deleted', t('review.deleted', 'Deleted')],
                          ['held', t('review.held', 'Need attention')], ['blocked', t('review.notImported', 'Not imported (waiting / blocked)')]]
                            .map(([key, label]) => (
                                <div key={key} className="border rounded px-3 py-2 bg-white">
                                    <div className="h5 mb-0">{totals[key as string] || 0}</div>
                                    <div className="small text-muted">{label}</div>
                                </div>
                            ))}
                    </div>
                )}

                {(session.after_sync_through || 0) > 0 && ['ready', 'applying', 'applied'].includes(session.status) && (
                    <div className="alert alert-light border small" role="note">
                        {tx('review.afterSyncThrough', "QuickBooks also has {{count}} transaction(s) after {{month}}. They'll come in with a later re-sync.",
                            { count: session.after_sync_through, month: monthLabel(session.sync_through) })}
                    </div>
                )}

                {months.length === 0 && ready && (
                    <div className="alert alert-light border">{t('review.noChanges', 'No new months or transactions were found since the last sync.')}</div>
                )}

                {months.length > 0 && (
                    <div className="table-responsive bg-white border rounded">
                        <table className="table mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th>{t('review.month', 'Month')}</th>
                                    <th>{t('review.status', 'Status')}</th>
                                    <th className="text-end">{t('review.new', 'New')}</th>
                                    <th className="text-end">{t('review.edited', 'Edited')}</th>
                                    <th className="text-end">{t('review.deleted', 'Deleted')}</th>
                                    <th>{t('review.notes', 'Notes')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {months.map((m) => {
                                    const rows = open[m.month];
                                    return (
                                        <React.Fragment key={m.month}>
                                            <tr className={isSelectable(m) ? '' : 'text-muted'}>
                                                <td>
                                                    <input type="checkbox" aria-label={t('review.select', 'Import {{month}}', { month: monthLabel(m.month) })}
                                                           checked={m.selected} disabled={!ready || busy || !isSelectable(m)}
                                                           onChange={() => toggleMonth(m)} />
                                                </td>
                                                <td className="fw-bold">{monthLabel(m.month)}</td>
                                                <td><span className={`badge ${STATUS_PILL[m.status]}`}>{tx(`monthStatus.${m.status}`, MONTH_STATUS_TEXT[m.status] || m.status)}</span></td>
                                                <td className="text-end">{m.new || '–'}</td>
                                                <td className="text-end">{m.edited || '–'}</td>
                                                <td className="text-end">{m.deleted || '–'}</td>
                                                <td className="small">
                                                    {m.includes_month_import && m.selected && <div>{t('review.monthImport', 'Balance Sheet and P&L for the month are imported too.')}</div>}
                                                    {(session.already_imported || {})[m.month] > 0 && (
                                                        <div>{tx('review.alreadyImported', 'Already in RevRex: {{count}} transaction(s) -- reconciled and closed on Apply.',
                                                            { count: session.already_imported[m.month] })}</div>
                                                    )}
                                                    {m.reasons.map((r) => <div key={r} className="text-warning-emphasis">{tx(`reason.${r}`, REASON_TEXT[r] || r)}</div>)}
                                                </td>
                                                <td>
                                                    {m.total > 0 && (
                                                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => toggleView(m.month)}>
                                                            {rows ? t('review.hide', 'Hide') : t('review.view', 'View')}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {rows && (
                                                <tr>
                                                    <td></td>
                                                    <td colSpan={7}>
                                                        {rows === 'loading' ? <Spinner animation="border" size="sm" /> : (
                                                            <table className="table table-sm mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>{t('review.type', 'Type')}</th><th>{t('review.number', 'No.')}</th>
                                                                        <th>{t('review.name', 'Name')}</th><th>{t('review.date', 'Date')}</th>
                                                                        <th className="text-end">{t('review.amount', 'Amount')}</th>
                                                                        <th>{t('review.change', 'Change')}</th><th>{t('review.result', 'Result')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {rows.map((c: any) => (
                                                                        <tr key={c.id}>
                                                                            <td>{entityLabel(c.entity_type)}</td>
                                                                            <td>{c.ref_number || '—'}</td>
                                                                            <td>{c.name || '—'}</td>
                                                                            <td>{c.txn_date || '—'}</td>
                                                                            <td className="text-end">{c.amount ?? '—'}</td>
                                                                            <td>{tx(`change.${c.change_type}`, CHANGE_TEXT[c.change_type] || c.change_type)}</td>
                                                                            <td className="small">
                                                                                {c.decision === 'apply' && c.apply_status !== 'pending'
                                                                                    ? tx(`applyStatus.${c.apply_status}`, APPLY_STATUS_TEXT[c.apply_status] || c.apply_status)
                                                                                    : (c.reasons || []).map((r: string) => tx(`reason.${r}`, REASON_TEXT[r] || r)).join('; ')
                                                                                      || tx(`decision.${c.decision}`, DECISION_TEXT[c.decision] || c.decision)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 mb-4">
                    <div className="small text-muted">
                        {gap
                            ? <span className="text-danger">{t('review.gap', 'Months are imported in order: tick {{month}} first, or untick the months after it.', { month: monthLabel(gap) })}</span>
                            : ready && t('review.selected', '{{n}} month(s) selected', { n: chosen.length })}
                    </div>
                    <div className="d-flex gap-2">
                        {['collecting', 'ready'].includes(session.status) && (
                            <button type="button" className="btn btn-cancel" onClick={() => setConfirmCancel(true)}>
                                {t('review.cancel', 'Cancel re-sync')}
                            </button>
                        )}
                        {ready && (
                            <button type="button" className="btn btn-primary" disabled={busy || !chosen.length || !!gap}
                                    onClick={() => setConfirmApply(true)}>
                                {t('review.apply', 'Apply selected')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <SAlert
                show={confirmApply}
                title={t('review.confirmTitle', 'Apply these changes?')}
                msg={t('review.confirmMsg', '{{months}} will be imported from {{name}}.', { months: chosen.map(monthLabel).join(', '), name })}
                icon="question"
                showCancel
                confirmBtnText={t('review.apply', 'Apply selected')}
                cancelBtnText={t('common.cancel', 'Cancel')}
                confirmBtnBsStyle="primary"
                onConfirm={apply}
                onCancel={() => setConfirmApply(false)}
            />
            <SAlert
                show={confirmCancel}
                title={t('review.cancelTitle', 'Cancel this re-sync?')}
                msg={t('review.cancelMsg', 'Nothing will be imported. You can start a new re-sync any time.')}
                icon="warning"
                showCancel
                confirmBtnText={t('review.cancel', 'Cancel re-sync')}
                cancelBtnText={t('review.keep', 'Keep it')}
                confirmBtnBsStyle="danger"
                onConfirm={cancel}
                onCancel={() => setConfirmCancel(false)}
            />
        </div>
    );
};

export default QbSyncReview;
